import contract from '../../../catalogs/janus/row-contract.json';
import { fromBaseRecord, fromBaseUuid } from '../compile/from-base-context';
import { readItemField } from '../compile/item-field-reader';
import { utf8Bytes } from '../compile/php-json-serialize';
import type { SpawnRowPlan } from './row-plan';

interface RowContract {
    key: string; key_type?: string; table?: string; extends?: string;
    columns: readonly string[]; extra_columns?: readonly string[]; context_columns?: readonly string[];
    updates: readonly string[]; required: readonly string[]; derived?: readonly string[];
    before?: readonly string[]; operations?: readonly string[]; numbers: readonly string[]; decimals?: readonly string[];
    required_references?: readonly string[]; values?: Readonly<Record<string, readonly unknown[]>>;
}
const raw = contract as unknown as Readonly<Record<string, RowContract>>;
export const spawnRowContracts: Readonly<Record<string, RowContract>> = Object.fromEntries(Object.entries(raw).map(([kind, spec]) => {
    const parent = spec.extends ? raw[spec.extends] : undefined;
    return [kind, parent ? { ...parent, ...spec, columns: [...parent.columns, ...spec.extra_columns ?? []] } : spec];
}));
const textWithin = (value: unknown, bytes: number): value is string => {
    if (typeof value !== 'string' || value.length > bytes) return false;
    try { return utf8Bytes(value).length <= bytes; } catch { return false; }
};
const logKinds = new Set(['added_creatures', 'add_inventory', 'summoned_creature', 'creature_transform', 'janus_unported_skip']);
const onlyKeys = (row: Record<string, unknown>, allowed: readonly string[]): boolean => Object.keys(row).every(key => allowed.includes(key));

/** Completed browser decisions are admitted here, before publication or a lazy save. Never mutates held rows. */
export function admitSpawnRowPlan<T extends SpawnRowPlan>(plan: T): T {
    if (!fromBaseRecord(plan) || plan.version !== 'janus.spawn.rows.v1' || !Array.isArray(plan.writes) || plan.writes.length > 4096) throw new Error('spawn:plan-values');
    const seen = new Set<string>();
    for (const write of plan.writes) {
        if (!fromBaseRecord(write)) throw new Error('spawn:row-values');
        const spec = spawnRowContracts[write.table];
        if (!spec || !(spec.operations ?? ['insert', 'update']).includes(write.op)) throw new Error(`spawn:row-kind:${write.table}`);
        const row = write.row;
        if (!fromBaseRecord(row)) throw new Error('spawn:row-columns');
        const id = readItemField(row, spec.key);
        if (spec.key_type === 'integer' ? !Number.isSafeInteger(id) || Number(id) <= 0 || Number(id) > 100000000 : !fromBaseUuid(id))
            throw new Error('spawn:row-identity');
        const identity = (spec.table ?? write.table) + ':' + String(id).toUpperCase();
        if (seen.has(identity)) throw new Error('spawn:duplicate-row');
        seen.add(identity);
        const allowed = (write.op === 'insert' ? [...spec.columns, ...spec.context_columns ?? []] : [spec.key, ...spec.updates])
            .filter(column => !spec.derived?.includes(column));
        const present = Object.entries(row).filter(([, value]) => value !== undefined).map(([column]) => column);
        if (!onlyKeys(row, allowed) || !(write.op === 'insert' ? spec.required : [spec.key]).every(column => present.includes(column))
            || write.op === 'update' && Object.keys(row).length < 2) throw new Error('spawn:row-columns');
        for (const [values, columns] of [[row, allowed], [write.before ?? {}, spec.before ?? allowed]] as const) {
            if (!fromBaseRecord(values) || !onlyKeys(values, columns)) throw new Error('spawn:row-before');
            // Serialized writes are flat SQL values; the producers already resolved held lean/wrapped fields through their readers.
            for (const [column, value] of Object.entries(values)) {
                if (value == null) continue;
                if (spec.numbers.includes(column)) {
                    if (typeof value !== 'number' || !Number.isFinite(value) || Math.abs(value) > 100000000
                        || !spec.decimals?.includes(column) && !Number.isInteger(value)) throw new Error('spawn:row-number');
                } else if (!textWithin(value, 262144)) throw new Error('spawn:row-text');
            }
        }
        for (const column of spec.required_references ?? []) if (!fromBaseUuid(readItemField(row, column))) throw new Error('spawn:required-reference');
        for (const [column, values] of Object.entries(spec.values ?? {}))
            if (Object.prototype.hasOwnProperty.call(row, column) && !values.includes(readItemField(row, column))) throw new Error('spawn:row-value');
    }
    if (plan.logs != null) {
        if (!Array.isArray(plan.logs) || plan.logs.length > 4096) throw new Error('spawn:log-values');
        for (const log of plan.logs) {
            if (!fromBaseRecord(log) || !onlyKeys(log, ['kind', 'name', 'detail', 'actor_id', 'arrival']) || !logKinds.has(log.kind)) throw new Error('spawn:log-kind');
            if (!textWithin(log.name, 1024) || !textWithin(log.detail, 16384)) throw new Error('spawn:log-text');
            if (log.actor_id != null && !fromBaseUuid(readItemField(log, 'actor_id'))) throw new Error('spawn:log-actor');
            if (log.arrival != null && (log.kind !== 'summoned_creature' || log.actor_id == null)) throw new Error('spawn:log-arrival');
        }
    }
    return plan;
}
